import { type ApiResponse } from '@/api/core/_models';
import { checklistMoment, createMockChecklistRuns, DEFAULT_ACTOR, findMockChecklistTemplate } from './_mock';
import {
  type ChecklistActivityRef,
  type ChecklistAnswer,
  type ChecklistAnswerChange,
  type ChecklistAnswerInput,
  type ChecklistCheckoutInput,
  ChecklistOrigin,
  type ChecklistRun,
  type ChecklistRunDetail,
  ChecklistRunStatus,
} from './_models';
import {
  buildChecklistAnswer,
  countAnsweredQuestions,
  findChecklistQuestion,
  isChecklistClosed,
  missingRequiredNumbers,
  scoreChecklist,
} from './_utils';

export { DEFAULT_ACTOR } from './_mock';

const ACTIVITY_MANDATORY_TEMPLATE_ID = 'abastecimento';
const ACTIVITY_OPTIONAL_TEMPLATE_ID = 'validade';

function resolveMockResponse<T>(data: T): Promise<ApiResponse<T>> {
  const responseData = structuredClone(data);
  const response: ApiResponse<T> = { success: true, status: 200, data: responseData, length: 1 };
  return Promise.resolve(response);
}

function rejectMockResponse(status: number, message: string): Promise<never> {
  const error = new Error(message);
  const responseError = Object.assign(error, { status });
  return Promise.reject(responseError);
}

function mergeAnswerChange(stored: ChecklistAnswer | undefined, change: ChecklistAnswerChange): ChecklistAnswerInput {
  return {
    questionId: change.questionId,
    value: change.value ?? stored?.value,
    notApplicable: change.notApplicable ?? stored?.notApplicable ?? false,
    comment: change.comment ?? stored?.comment ?? '',
    photos: change.photos ?? stored?.photos ?? [],
  };
}

function hasNoAnswerContent(value: string | undefined, notApplicable: boolean, photoCount: number, comment: string) {
  return !value && !notApplicable && photoCount === 0 && comment.trim() === '';
}

export function createMockChecklistRequests(now: () => Date = () => new Date()) {
  const runs: ChecklistRun[] = createMockChecklistRuns(now());

  function statusOf(status: ChecklistRunStatus, dueAt: string, startedAt?: string): ChecklistRunStatus {
    if (isChecklistClosed(status)) return status;
    if (now().getTime() > new Date(dueAt).getTime()) return ChecklistRunStatus.LATE;
    return startedAt ? ChecklistRunStatus.IN_PROGRESS : ChecklistRunStatus.PENDING;
  }

  function presentRun(run: ChecklistRun): ChecklistRun {
    return { ...run, status: statusOf(run.status, run.dueAt, run.startedAt) };
  }

  function presentDetail(run: ChecklistRun): ChecklistRunDetail {
    return { ...presentRun(run), template: findMockChecklistTemplate(run.templateId) };
  }

  function listRuns() {
    return resolveMockResponse(runs.map(presentRun));
  }

  function getRun(id: string) {
    const run = runs.find((item) => item.id === id);
    if (!run) return rejectMockResponse(404, 'Este checklist não existe.');
    return resolveMockResponse(presentDetail(run));
  }

  function markStarted(run: ChecklistRun, actor: string, location?: string) {
    if (run.startedAt) {
      if (location && !run.startLocation) run.startLocation = location;
      return;
    }
    run.startedAt = now().toISOString();
    run.startedBy = actor;
    run.startLocation = location;
  }

  function startRun(id: string, location?: string, actor = DEFAULT_ACTOR) {
    const run = runs.find((item) => item.id === id);
    if (!run) return rejectMockResponse(404, 'Este checklist não existe.');
    if (isChecklistClosed(run.status)) return rejectMockResponse(409, 'Este checklist já foi encerrado.');
    markStarted(run, actor, location);
    return resolveMockResponse(presentDetail(run));
  }

  function saveAnswer(id: string, change: ChecklistAnswerChange, actor = DEFAULT_ACTOR) {
    const run = runs.find((item) => item.id === id);
    if (!run) return rejectMockResponse(404, 'Este checklist não existe.');
    if (isChecklistClosed(run.status)) return rejectMockResponse(409, 'Este checklist já foi encerrado.');
    const template = findMockChecklistTemplate(run.templateId);
    const question = findChecklistQuestion(template.sections, change.questionId);
    if (!question) return rejectMockResponse(400, 'Questão não encontrada.');
    const stored = run.answers.find((answer) => answer.questionId === change.questionId);
    const input = mergeAnswerChange(stored, change);
    if (input.notApplicable && !question.allowsNotApplicable) {
      return rejectMockResponse(400, "Esta questão não aceita 'não se aplica'.");
    }
    markStarted(run, actor);
    const others = run.answers.filter((answer) => answer.questionId !== input.questionId);
    const answer = buildChecklistAnswer(question.options, input, actor, now().toISOString());
    const isEmptyAnswer = hasNoAnswerContent(input.value, input.notApplicable, input.photos.length, input.comment);
    run.answers = isEmptyAnswer ? others : [...others, answer];
    run.progress = { answered: countAnsweredQuestions(template.sections, run.answers), total: run.progress.total };
    return resolveMockResponse(presentDetail(run));
  }

  function sendRun(id: string, input: ChecklistCheckoutInput, actor = DEFAULT_ACTOR) {
    const run = runs.find((item) => item.id === id);
    if (!run) return rejectMockResponse(404, 'Este checklist não existe.');
    if (isChecklistClosed(run.status)) return rejectMockResponse(409, 'Este checklist já foi encerrado.');
    const comment = input.comment.trim();
    if (!comment) return rejectMockResponse(400, 'Escreva o comentário final.');
    if (!input.signatureUrl) return rejectMockResponse(400, 'Assine antes de enviar.');
    const template = findMockChecklistTemplate(run.templateId);
    const missingNumbers = missingRequiredNumbers(template.sections, run.answers);
    if (missingNumbers.length > 0) {
      return rejectMockResponse(400, `Responda as questões obrigatórias: ${missingNumbers.join(', ')}.`);
    }
    const sentAt = now().toISOString();
    run.status = ChecklistRunStatus.SENT;
    run.sentAt = sentAt;
    run.sentBy = actor;
    run.score = scoreChecklist(template.sections, run.answers);
    run.checkout = {
      comment,
      signatureUrl: input.signatureUrl,
      signedBy: actor,
      signedAt: sentAt,
      location: input.location,
    };
    return resolveMockResponse(presentDetail(run));
  }

  function createActivityRun(activity: ChecklistActivityRef, templateId: string, mandatory: boolean): ChecklistRun {
    const template = findMockChecklistTemplate(templateId);
    return {
      id: globalThis.crypto.randomUUID(),
      templateId,
      templateName: template.name,
      coverUrl: template.coverUrl,
      store: activity.store,
      origin: ChecklistOrigin.ACTIVITY,
      activity: { taskId: activity.taskId, label: activity.label, path: activity.path, mandatory },
      dueAt: checklistMoment(now(), 0, '20:00'),
      status: ChecklistRunStatus.PENDING,
      progress: { answered: 0, total: template.sections.reduce((total, item) => total + item.questions.length, 0) },
      answers: [],
    };
  }

  function runsOfTask(taskId: string): ChecklistRun[] {
    return runs.filter((run) => run.activity?.taskId === taskId);
  }

  function getActivityRuns(activity: ChecklistActivityRef) {
    if (runsOfTask(activity.taskId).length === 0) {
      const mandatoryRun = createActivityRun(activity, ACTIVITY_MANDATORY_TEMPLATE_ID, true);
      const optionalRun = createActivityRun(activity, ACTIVITY_OPTIONAL_TEMPLATE_ID, false);
      runs.push(mandatoryRun, optionalRun);
    }
    return resolveMockResponse(runsOfTask(activity.taskId).map(presentRun));
  }

  function skipOptionalRun(id: string) {
    const run = runs.find((item) => item.id === id);
    if (!run) return rejectMockResponse(404, 'Este checklist não existe.');
    if (isChecklistClosed(run.status)) return rejectMockResponse(409, 'Este checklist já foi encerrado.');
    if (run.activity?.mandatory !== false) return rejectMockResponse(409, 'Este checklist é obrigatório.');
    run.status = ChecklistRunStatus.NOT_DONE;
    return resolveMockResponse(presentRun(run));
  }

  function cancelActivityRuns(taskId: string) {
    const taskRuns = runsOfTask(taskId);
    taskRuns
      .filter((run) => !isChecklistClosed(run.status))
      .forEach((run) => {
        run.status = ChecklistRunStatus.CANCELED;
      });
    return resolveMockResponse(taskRuns.map(presentRun));
  }

  return { listRuns, getRun, startRun, saveAnswer, sendRun, getActivityRuns, skipOptionalRun, cancelActivityRuns };
}
